import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import type { AssessmentGovernanceTab } from '~/client/components/assessment/utils/assessment-utils';
import * as GQL from '~/graphql/generated/client';
import { getDesignTokens } from '~c/components/base/core';
import Text, { TextType } from '~c/components/base/text';
import Tooltip from '~c/components/base/tooltip';

interface Props {
  className?: string;
  policy: GQL.PolicyFragment;
  /**
   * Specify the gap between rows
   * i.e. gap between `policy name` and `identifier · version`
   */
  gap?: number;
  /**
   * Size of text (default body14)
   */
  textSz?: TextType;
  /**
   * When set (e.g. project governance table), icon and tag style follow the
   * same buckets as dashboard governance tabs (Assignment / ManageSearch / Science).
   */
  governanceTab?: AssessmentGovernanceTab;
}

const PolicyItemImpl: React.FC<Props> = ({
  className,
  policy,
  textSz = 'body14',
  governanceTab,
}) => {
  const { identifier, version, name, labels } = policy;

  const isAssurance = labels?.some((label) =>
    label.toLowerCase().includes('assurance')
  );

  const tooltipText = `${identifier} · ${version}`;

  let IconComponent: React.ComponentType<{ className?: string }>;
  let tagClass: 'policy' | 'assurance';
  if (governanceTab) {
    if (governanceTab === 'testing') {
      IconComponent = ScienceOutlined;
      tagClass = 'assurance';
    } else if (governanceTab === 'screening') {
      IconComponent = ManageSearchOutlined;
      tagClass = 'policy';
    } else {
      IconComponent = AssignmentOutlined;
      tagClass = 'policy';
    }
  } else {
    IconComponent = isAssurance ? ScienceOutlined : GavelOutlined;
    tagClass = isAssurance ? 'assurance' : 'policy';
  }

  return (
    <Tooltip title={tooltipText} arrow>
      <div className={className}>
        <div className="policy-item-container">
          <div className={`policy-tag ${tagClass}`}>
            <IconComponent className="policy-tag-icon" />
          </div>
          <Text sz={textSz} className="policy-name">
            {name}
          </Text>
        </div>
      </div>
    </Tooltip>
  );
};

export const PolicyItem = muiStyled(PolicyItemImpl)(({ theme }) => {
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette
  );

  return css`
    .policy-item-container {
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-radius: 6px;
      padding: 8px 12px;
      margin: -8px -12px;
    }

    .policy-tag {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: ${t.radiusSmall}px;
      flex-shrink: 0;
      transition: all 0.2s ease;
    }

    .policy-tag:hover {
      transform: scale(1.1);
    }

    .policy-tag-icon {
      font-size: 16px;
    }

    .policy-tag.policy {
      background: ${t.accentBg};
      border: 1px solid ${t.accent}33;
      color: ${t.accent};
    }

    .policy-tag.assurance {
      background: ${t.status.successBg};
      border: 1px solid ${t.status.success}33;
      color: ${t.status.success};
    }

    .policy-name {
      font-weight: 500;
      line-height: 1.4;
      color: ${theme.palette.text.primary};
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `;
});

export default PolicyItem;
